<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { tConversation } from '@/plugins/i18n';
import type { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import type { ConnectToAgent } from '@/common/bot/types/config';
import type { BetHistoryEntity, BetHistoryEntitySelection, TransStatement } from '@/common/api/model';
import TransactionList from './TransactionList.vue';
import BetTransactionItem from './BetTransactionItem.vue';
import { useBetTransactions } from './composables/useBetTransactions';
import type { TransactionSelectEvent, TransactionSubmitEvent } from './types';
import { isBetTransactionType } from '@/common/transaction-utils';

const TRANSACTIONS_PER_PAGE = 3;
const MIN_SELECTIONS_FOR_EVENT_SELECTION = 2;
const VALIDATE_SELECTION_FOR_USER_EVENTS = true;

export interface BetTransactionMessageProps {
  transactionSubtypes: ExternalTransactionKind[];
  connectConfig?: ConnectToAgent | null;
  showEventSelection?: boolean;
  canSubmit?: boolean;
  cancelable?: boolean;
  /**
   * Pre-selected transactions from selected entity flow.
   * These will be shown first and auto-selected.
   */
  preSelectedTransactions?: Array<BetHistoryEntity | TransStatement>;
  /** When true, only single transaction can be selected (multi-select blocked). Default false = allow multi-select. */
  singleSelectOnly?: boolean;
}

const props = withDefaults(defineProps<BetTransactionMessageProps>(), {
  showEventSelection: false,
  preSelectedTransactions: () => [],
});

const emit = defineEmits<{
  (e: 'select', event: TransactionSelectEvent): void;
  (e: 'submit', event: TransactionSubmitEvent): void;
  (e: 'scroll-bottom'): void;
}>();

const {
  transactions: fetchedTransactions,
  loading,
  error,
  hasMore,
  loadMore,
} = useBetTransactions({
  transactionSubtypes: props.transactionSubtypes,
  pageSize: TRANSACTIONS_PER_PAGE,
});

const userEventsSelections = ref<BetHistoryEntitySelection[]>([]);

// Filter pre-selected transactions to only include BetHistoryEntity types
const preSelectedBets = computed(() => {
  return (props.preSelectedTransactions || []).filter(isBetTransactionType);
});

// Seed local selection so event-selection UI works when a bet arrives pre-selected
const selectedTransaction = ref<BetHistoryEntity | null>(preSelectedBets.value[0] ?? null);

// Helper to check if a transaction is a duplicate based on bet_id
const isDuplicateTransaction = (transaction: BetHistoryEntity, list: BetHistoryEntity[]) => {
  return list.some((t) => t.shortId === transaction.shortId);
};

// Merge pre-selected and fetched transactions
// Pre-selected transactions come first, duplicates from fetched are filtered out
const transactions = computed(() => {
  const preSelected = preSelectedBets.value;
  const fetched = fetchedTransactions.value;

  // Filter out fetched transactions that are already in pre-selected
  const uniqueFetched = fetched.filter((transaction) => !isDuplicateTransaction(transaction, preSelected));

  // Return pre-selected first, then unique fetched
  return [...preSelected, ...uniqueFetched];
});

const multiSelect = computed(() => !props.singleSelectOnly);

const submitButtonText = computed(() => tConversation('page.guest.button.proceed'));

const shouldShowEventSelection = computed(() => {
  return (
    props.showEventSelection &&
    selectedTransaction.value &&
    (selectedTransaction.value.selections?.length || 0) >= MIN_SELECTIONS_FOR_EVENT_SELECTION
  );
});

const canEdit = computed(() => {
  return Boolean(props.canSubmit);
});
const canSubmit = computed(() => {
  return Boolean(
    props.canSubmit &&
    !(VALIDATE_SELECTION_FOR_USER_EVENTS && shouldShowEventSelection.value && !userEventsSelections.value.length)
  );
});

const handleSelect = (event: TransactionSelectEvent) => {
  const transaction = event.transaction as BetHistoryEntity;

  if (event.selected) {
    selectedTransaction.value = transaction;
    // Reset user events selections when selecting a new transaction
    userEventsSelections.value = [];
  } else {
    selectedTransaction.value = null;
    userEventsSelections.value = [];
  }

  emit('select', event);
};

const handleUserEventToggle = (selection: BetHistoryEntitySelection) => {
  const isSelected = userEventsSelections.value.some((s) => s.id === selection.id);

  if (isSelected) {
    const index = userEventsSelections.value.findIndex((s) => s.id === selection.id);
    if (index > -1) {
      userEventsSelections.value.splice(index, 1);
    }
  } else {
    userEventsSelections.value.push(selection);
  }
};

const handleSubmit = (event: TransactionSubmitEvent) => {
  const transactions = event.transactions.filter(isBetTransactionType).map((transaction) => {
    return {
      ...transaction,
      // Current hardcoded field for saving transactions and same for AI layer
      __USER_EVENTS: transaction.selections?.filter((selection) =>
        userEventsSelections.value.some((s) => s.id === selection.id)
      ),
    };
  });

  emit('submit', { transactions });
};

// watch for data, once loaded – scroll to bottom
watch(
  transactions,
  () => {
    if (transactions.value.length > 0) {
      emit('scroll-bottom');
    }
  },
  { immediate: true }
);
</script>

<template>
  <TransactionList
    :transactions="transactions"
    :transaction-subtypes="transactionSubtypes"
    :initial-selected-transactions="preSelectedBets"
    :show-load-more="hasMore"
    :loading="loading"
    :error="error"
    :submit-button-text="submitButtonText"
    :can-submit="canSubmit"
    :editable="canEdit"
    :cancelable="cancelable"
    :multi-select="multiSelect"
    @select="handleSelect"
    @submit="handleSubmit"
    @load-more="loadMore"
  >
    <template #transaction-item="{ transaction, isSelected, onToggle }">
      <BetTransactionItem
        v-if="isBetTransactionType(transaction)"
        :transaction="transaction"
        :is-selected="isSelected"
        :editable="canEdit"
        :show-event-selection="showEventSelection"
        :selected-user-events="userEventsSelections"
        :validate-selection-for-user-events="VALIDATE_SELECTION_FOR_USER_EVENTS"
        @toggle="onToggle"
        @toggle-user-event="handleUserEventToggle"
      />
    </template>
  </TransactionList>
</template>
